import React from 'react';
import { Link } from "react-router-dom";
import Highlight from 'src/components/Highlight';
import routes from 'src/routes';
import url from 'src/util/url';
import Page from 'src/components/Page';

function TxnGooglePayPage() {
    return <Page header="Google Pay&trade;">
        <p>
            Google Pay&trade; is an easy and secure payment method for your customers. With Google Pay, your customers can check out from your online store using an Android device, or via most web browsers.
        </p>
        <h3>
            How to enable Google Pay on the BPOINT Hosted Payment Page
        </h3>
        <p>
            To enable Google Pay on your BPOINT Hosted Payment Page, please contact your Relationship Manager or our Merchant Support Helpdesk on 1800 230 177 (option 1, 3, 1) Monday to Friday 9am to 5pm (AEST).
        </p>
        <h3>
            How to enable Google Pay on the BPOINT API
        </h3>
        <p>
            Please follow the instructions below to integrate with the BPOINT API and add the Google Pay button to your own payment page or Android device. You will need Google Pay enabled by your Relationship Manager; then you will need to use BackOffice to add Google Pay as a payment method on any desired billers.
        </p>
        <p>
            If you have any questions about the process or require any support with the BPOINT API, please contact your Relationship Manager or our Merchant Support Helpdesk on 1800 230 177 (option 1, 3, 1) Monday to Friday 9am to 5pm (AEST). 
        </p>
        <h3>
            Adding the Google Pay button to your Android device
        </h3>
        <p>
            To add Google Pay support to your Android device, follow the advice of the following guides:
            <ul>
              <li><a href={'https://developers.google.com/pay/api/android'}>Google Pay Android developer documentation</a></li>
              <li><a href={'https://developers.google.com/pay/api/android/guides/test-and-deploy/integration-checklist'}>Google Pay Android integration checklist</a></li>
              <li><a href={'https://developers.google.com/pay/api/android/guides/brand-guidelines'}>Google Pay Android brand guidelines</a></li>
            </ul>
        </p>
        <h3>
            Adding the Google Pay button to your online checkout page
        </h3>
        <p>
            To add Google Pay support to your online checkout page, follow the advice of the following guides:
            <ul>
            <li><a href={'https://developers.google.com/pay/api/web'}>Google Pay Web developer documentation</a></li>
            <li><a href={'https://developers.google.com/pay/api/web/guides/test-and-deploy/integration-checklist'}>Google Pay Web integration checklist</a></li>
            <li><a href={'https://developers.google.com/pay/api/web/guides/brand-guidelines'}>Google Pay Web Brand Guidelines</a></li>
            </ul>
        </p>
        <h3>
            Integrating Google Pay with the BPOINT API
        </h3>
        <p>
            Whichever of the above methods you integrate with, the following API workflow is necessary:
        </p>
        <h3>1. <Link to={routes.api.spec + "#operation/TxnAuthkey_CreateAuthkey"}>Create txn authkey</Link></h3>
        <h3>2. <Link to={routes.api.spec + "#operation/TxnAuthkey_AttachTxnDetails"}>Attach txn details</Link></h3>
        <h3>3. <Link to={routes.api.spec + "#operation/TxnAuthkeyClient_AttachGooglePayPaymentToken"}>Attach Google Pay payment token as a payment method</Link> (see details below)</h3>
        <h3>4. <Link to={routes.api.spec + "#operation/TxnAuthkey_Process"}>Process</Link></h3>
        <p>
        Integrating with Google Pay requires you to adhere to the <a href={'https://payments.developers.google.com/terms/aup'}>Acceptable Use Policy</a> and accept the terms specified in the <a href={'https://payments.developers.google.com/terms/sellertos'}>Google Pay API Terms of Service</a>.
        </p>
        <h3>
        Attach Google Pay payment token as a payment method
        </h3>
        <p>
          As specified in the Google tutorial, embedding the Google Pay button on your online checkout page involves:
          <ol>
            <li> embedding their JS script (<code>https://pay.google.com/gp/p/js/pay.js</code>)</li>
            <li> adding a container element (<code>{`<div id="container"></div>`}</code>) to the payment page; which will embed the Google Pay button</li>
            <li> on your payment page load event, calling <code>isReadyToPay()</code> and <code>createButton()</code>; causing the Google Pay button to be displayed</li>
            <li> setting the Google Pay button's <code>onClick</code> event to call <code>loadPaymentData()</code></li>
            <li>The response data of <code>loadPaymentData(req)</code> must be <code>JSON.stringify()</code>-ed and passed (verbatim) to BPOINT by attaching it to the AuthKey session by calling BPOINT API endpoint: <Link to={routes.api.spec + "#operation/TxnAuthkeyClient_AttachGooglePayPaymentToken"}>Attach Google Pay payment token as a payment method</Link></li>
          </ol>
        </p>
        <p>In addition to what's mentioned in the Google tutorials, the Google API call requests must follow these specifications:</p>

        <ul>
            <li>The <code>tokenizationSpecification</code> object must be defined as follows:
            <Highlight language={'javascript'}>
            {`const tokenizationSpecification = {
  type: 'PAYMENT_GATEWAY',
  parameters: {
    'gateway': 'linkly',
    'gatewayMerchantId': '<your_merchant_number>'
  }
};`}
            </Highlight>
            <p>Only <code>gatewayMerchantId</code> should be customised with your BPOINT merchant number.</p>
            </li>
            <li>The <code>allowedCardNetworks</code> array must be set to the list of card networks supported by the merchant. For example:
            <Highlight language={'javascript'}>
            const allowedCardNetworks = ["AMEX", "JCB", "MASTERCARD", "VISA"];
            </Highlight>
            </li>
            <li>The <code>allowedCardAuthMethods</code> array must be defined as follows:
            <Highlight language={'javascript'}>
            const allowedCardAuthMethods = ["PAN_ONLY", "CRYPTOGRAM_3DS"];
            </Highlight>
            </li>
            <li>Following the advice above, the call to Google API <code>isReadyToPay(req)</code> request should be:
            <Highlight language={'json'}>
                {`{
    "apiVersion": 2,
    "apiVersionMinor": 0,
    "allowedPaymentMethods": [
      {
        "type": "CARD",
        "parameters": {
          "allowedAuthMethods": [
            "PAN_ONLY",
            "CRYPTOGRAM_3DS"
          ],
          "allowedCardNetworks": [
            "AMEX",
            "JCB",
            "MASTERCARD",
            "VISA"
          ]
        }
      }
    ]
}`}
            </Highlight>
            <p><code>allowedCardNetworks</code> must be mapped from your biller's allowed card networks.</p>
            </li>
            <li>The call to Google API <code>createButton(options)</code> request should be:
            <Highlight language={'json'}>
                {`{
    "type": "CARD",
    "parameters": {
      "allowedAuthMethods": [
        "PAN_ONLY",
        "CRYPTOGRAM_3DS"
      ],
      "allowedCardNetworks": [
        "AMEX",
        "JCB",
        "MASTERCARD",
        "VISA"
      ]
    }
}`}
            </Highlight>
            <p><code>allowedCardNetworks</code> must be mapped from your biller's allowed card networks.</p>
            </li>
            <li>The call to Google API <code>loadPaymentData(req)</code> request should be:
            <Highlight language={'json'}>
                {`{
    "apiVersion": 2,
    "apiVersionMinor": 0,
    "allowedPaymentMethods": [
      {
        "type": "CARD",
        "parameters": {
          "allowedAuthMethods": [
            "PAN_ONLY",
            "CRYPTOGRAM_3DS"
          ],
          "allowedCardNetworks": [
            "AMEX",
            "JCB",
            "MASTERCARD",
            "VISA"
          ]
        },
        "tokenizationSpecification": {
          "type": "PAYMENT_GATEWAY",
          "parameters": {
            "gateway": "linkly",
            "gatewayMerchantId": "<your_merchant_number>"
          }
        }
      }
    ],
    "transactionInfo": {
      "countryCode": "AU",
      "currencyCode": "AUD",
      "totalPriceStatus": "FINAL",
      "totalPrice": "21.00"
    },
    "merchantInfo": {
      "merchantId": "<your_google_issued_merchant_id>",
      "merchantName": "<your_merchant_name>",
      "merchantOrigin": "<hostname_of_your_online_checkout_page>"
    }
}`}
            </Highlight>
            <p><code>allowedCardNetworks</code> must be mapped from your biller's allowed card networks.</p>
            <p><code>gatewayMerchantId</code> must be set to your merchant number.</p>
            <p><code>countryCode</code> must be set to the 2-character ISO 3166 country code.</p>
            <p><code>currencyCode</code> must be set to the 3-character ISO 4217 currency code.</p>
            <p><code>totalPrice</code> must be set to the transaction amount (in dollars and cents, separated by decimal point). The value must not contain a thousands separator (like ','). For example, $2,198.70 must be submitted as <code>"totalPrice": "2198.70"</code>. This will be displayed on the Google Pay payment sheet.</p>
            <p><code>merchantId</code> must be set to your Google-issued merchant ID.</p>
            <p><code>merchantName</code> must be set to your merchant name. This will be displayed on the Google Pay payment sheet.</p>
            <p><code>merchantOrigin</code> must be set to the host name of the merchant online checkout page.</p>
            </li>
            <li>The JSON response data of <code>loadPaymentData(req)</code> must be passed on (verbatim) to BPOINT by passing it through <code>JSON.stringify()</code> and then attaching it to the AuthKey session by calling: <h3><Link to={routes.api.spec + "#operation/TxnAuthkeyClient_AttachGooglePayPaymentToken"}>Attach Google Pay payment token as a payment method</Link></h3>
            </li>
        </ul>


        <p/>

        <h2>JavaScript</h2>
        <p>To enable JavaScript integration, you must include the following script tag in your page.</p>
        <Highlight language={'html'}>
            {`<script src="${url.api("clientscripts/api.js")}" type="text/javascript"></script>`}
        </Highlight>
    </Page>
}
export default TxnGooglePayPage;